import { create } from "zustand";
import { FilterJobStatus, Job, SortByJobField } from "@/modules/types/job";
import { GetListJobResp, jobService } from "@/modules/services/job/jobService";
import { AppError } from "@/lib/utils/errors";
import { GetJobPayload } from "../types/job/store";
import { SortType } from "../shared/types/enum";

interface JobManagerState {
  jobs: Job[];
  total: number;
  isLoading: boolean;
  error?: string;
  limit: number;
  page: number;
  search: string;
  filterStatus?: FilterJobStatus;
  sortBy?: SortByJobField;
  sortOrder?: SortType;
}

interface JobManagerActions {
  getListJob: (payload: GetJobPayload) => Promise<void>;
  success: (payload: Partial<JobManagerState>) => void;
  setError: (error: string) => void;
  clearError: () => void;
  setJobs: (jobs: Job[]) => void;
  setTotal: (total: number) => void;
  setIsLoading: (isLoading: boolean) => void;
  appendJobs: (jobs: Job[]) => void;
  reset: () => void;
}

export type JobManagerStore = JobManagerState & JobManagerActions;

const initialState: JobManagerState = {
  jobs: [],
  total: 0,
  search: "",
  filterStatus: FilterJobStatus.all,
  isLoading: false,
  limit: 10,
  page: 1,
};

export const useJobManagerStore = create<JobManagerStore>((set) => ({
  ...initialState,

  getListJob: async (payload: GetJobPayload) => {
    set({ isLoading: true });
    try {
      const svc = new jobService();
      const resp: GetListJobResp = await svc.getsByBusiness(
        payload.businessId,
        payload.search,
        payload.page,
        payload.limit,
        undefined,
        payload.filterStatus,
        payload.sortBy,
        payload.sortOrder
      );
      set({
        jobs: resp.jobs,
        total: resp.total || 0,
        ...payload,
        isLoading: false,
      });
    } catch (err) {
      const appErr = new AppError(err);
      appErr.logError();
      set({ error: appErr.message, isLoading: false });
    }
  },

  success: (payload) => set((state) => ({ ...state, ...payload, isLoading: false })),
  setError: (error) => set({ error, isLoading: false }),
  clearError: () => set({ error: undefined }),
  setJobs: (jobs) => set({ jobs }),
  setTotal: (total) => set({ total }),
  setIsLoading: (isLoading) => set({ isLoading }),
  appendJobs: (jobs) => set((state) => ({ jobs: [...state.jobs, ...jobs] })),
  reset: () => set(initialState),
}));
